import { z } from 'zod'

export enum TaskType {
  Plant = 'plant',
  Sidedress = 'sidedress',
  Spray = 'spray',
  Striptill = 'striptill',
  Harvest = 'harvest'
}

/**
 * The kind of machine carrying the GPS receiver. Mirrors the 20|20's vehicle types.
 */
export enum VehicleType {
  Tractor = 'tractor',
  Combine = 'combine',
  SelfPropelledSprayer = 'self_propelled_sprayer'
}

/**
 * These are the valid crop types recognized throughout Panorama and on the 20|20.
 */
export enum CropType {
  Corn = 'Corn',
  Soybeans = 'Soybeans',
  Barley = 'Barley',
  EdibleBeanSmall = 'Edible Bean, Small',
  EdibleBeanMedium = 'Edible Bean, Medium',
  EdibleBeanLarge = 'Edible Bean, Large',
  Canola = 'Canola',
  Clover = 'Clover',
  SweetCorn = 'Sweet Corn',
  SeedCorn = 'Seed Corn',
  Cotton = 'Cotton',
  Flax = 'Flax',
  Meadowfoam = 'Meadowfoam',
  Mustard = 'Mustard',
  Oats = 'Oats',
  Onions = 'Onions',
  Peanuts = 'Peanuts',
  Peas = 'Peas',
  Popcorn = 'Popcorn',
  Pumpkins = 'Pumpkins',
  Radish = 'Radish',
  Rice = 'Rice',
  Ryegrass = 'Ryegrass',
  Rye = 'Rye',
  Sorghum = 'Sorghum',
  Squash = 'Squash',
  SugarBeets = 'Sugar Beets',
  SunflowersOil = 'Sunflowers, Oil',
  SunflowersConfectionary = 'Sunflowers, Confectionary',
  Watermelon = 'Watermelon',
  Wheat = 'Wheat',
  Other = 'Other'
}

/**
 * A stored custom crop name as a reader sees it. A blank name is as good as none, so a reader is
 * never handed one.
 */
export const storedCustomCropSchema = z
  .string()
  .trim()
  .transform((name) => (name === '' ? undefined : name))
  .optional()

/**
 * The string a stream is grouped, filtered, indexed, and reported by. A custom crop is recorded as
 * `CropType.Other` beside the grower's name for it, so the name is the identity; a built-in crop is
 * identified by its type.
 */
export const resolveCropKey = (stream: {
  crop: string
  customCrop?: string | undefined
}): string =>
  stream.crop === CropType.Other && stream.customCrop !== undefined
    ? stream.customCrop
    : stream.crop

export enum LiquidRateUnit {
  GallonsPerAcre = 'gal/ac',
  LitersPerHectare = 'L/ha',
  GallonsPerHectare = 'gal/ha',
  LitersPerAcre = 'L/ac',
  FluidOuncesPerAcre = 'fl oz/ac',
  MillilitersPerAcre = 'mL/ac',
  QuartsPerAcre = 'qt/ac',
  PintsPerAcre = 'pt/ac',
  FluidOuncesPerHectacre = 'fl oz/ha',
  MillilitersPerHectacre = 'mL/ha',
  QuartsPerHectare = 'qt/ha',
  PintsPerHectacre = 'pt/ha'
}

export enum GranularRateUnit {
  PoundsPerAcre = 'lbs/ac',
  KilogramsPerHectare = 'kg/ha',
  PoundsPerHectare = 'lbs/ha',
  KilogramsPerAcre = 'kg/ac',
  OuncesPerAcre = 'oz/ac',
  OuncesPerHectare = 'oz/ha',
  GramsPerAcre = 'g/ac',
  GramsPerHectare = 'g/ha'
}

export enum SeedingTank {
  Orange = 'Orange',
  Blue = 'Blue'
}

export enum ProductTank {
  Orange = 'Orange',
  Blue = 'Blue',
  Purple = 'Purple',
  Pink = 'Pink',
  Red = 'Red',
  Yellow = 'Yellow',
  Green = 'Green',
  Gray = 'Gray'
}

export enum ActiveRows {
  All = 'All',
  None = 'None',
  Even = 'Even',
  Odd = 'Odd',
  Left = 'Left',
  Right = 'Right',
  List = 'List'
}
